const DB_NAME = 'mpa-drafts';
const DB_VERSION = 1;
const STORE_NAME = 'recordings';
const EXPIRY_DAYS = 30;

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * After a full reload, iOS Safari often keeps IndexedDB blobs as lazy references:
 * `<audio>` can still play, but `blob.arrayBuffer()` throws NotFoundError
 * ("The object can not be found here.") when uploading. Copy bytes into a new Blob.
 */
export async function rehydrateDraftAudioBlob(blob) {
  if (!blob || typeof blob.arrayBuffer !== 'function') return blob;
  const type = blob.type || 'audio/mp4';
  const toBlob = (buffer) => new Blob([buffer], { type });

  try {
    return toBlob(await blob.arrayBuffer());
  } catch (e) {
    console.warn('[draftService] full arrayBuffer failed, rehydrating…', e?.message || e);
  }

  const size = blob.size;
  if (Number.isFinite(size) && size > 0) {
    try {
      const chunkSize = 256 * 1024;
      const parts = [];
      for (let offset = 0; offset < size; offset += chunkSize) {
        const end = Math.min(offset + chunkSize, size);
        const slice = blob.slice(offset, end);
        const buf = await slice.arrayBuffer();
        parts.push(new Uint8Array(buf));
      }
      const out = new Uint8Array(size);
      let pos = 0;
      for (const p of parts) {
        out.set(p, pos);
        pos += p.length;
      }
      return toBlob(out.buffer);
    } catch (e) {
      console.warn('[draftService] chunked slice failed', e?.message || e);
    }
  }

  try {
    const url = URL.createObjectURL(blob);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(String(res.status));
      const buf = await res.arrayBuffer();
      return toBlob(buf);
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (e) {
    console.warn('[draftService] object-URL fetch failed', e?.message || e);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(toBlob(reader.result));
      } else {
        reject(new Error('FileReader: invalid result'));
      }
    };
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.readAsArrayBuffer(blob);
  });
}

async function withRehydratedAudio(draft) {
  if (!draft?.audioBlob) return draft;
  try {
    const audioBlob = await rehydrateDraftAudioBlob(draft.audioBlob);
    return { ...draft, audioBlob };
  } catch (e) {
    console.error('[draftService] rehydrate failed for draft', draft.id, e);
    return draft;
  }
}

export async function saveDraft(draft) {
  const record = {
    ...draft,
    userId: localStorage.getItem('user_id'),
    createdAt: draft.createdAt || Date.now(),
  };
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(record);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function getAllDrafts() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => {
      db.close();
      const cutoff = Date.now() - EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      const valid = req.result.filter((d) => d.createdAt > cutoff);
      const expired = req.result.filter((d) => d.createdAt <= cutoff);
      if (expired.length) {
        expired.forEach((d) => deleteDraft(d.id).catch(() => {}));
      }
      const sorted = valid.sort((a, b) => b.createdAt - a.createdAt);
      Promise.all(sorted.map(withRehydratedAudio)).then(resolve).catch(reject);
    };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

export async function getDraft(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => {
      db.close();
      const row = req.result;
      if (!row) {
        resolve(null);
        return;
      }
      withRehydratedAudio(row).then(resolve).catch(reject);
    };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

export async function deleteDraft(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}
